import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  styled,
  Zoom,
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import LeftPanel from '../components/LeftPanel.tsx';
import { useThemeContext } from '../contexts/ThemeContext.tsx';
import { Particles } from '../components/Particles.tsx';

// --- Stílusok és Animációk ---
const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
`;

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(4px)',
    backgroundColor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.4)',
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: darkMode ? 'white' : '#1a237e',
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
  },
  '& .MuiDialogTitle-root': { 
    fontWeight: 'bold', 
    textAlign: 'center', 
    fontSize: '1.4rem',
    color: darkMode ? 'white' : '#1a237e',
    paddingTop: '24px'
  },
}));

const DialogActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '10px 32px',
  fontWeight: 'bold',
  color: '#ffffff',
  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.15)' : '#25379c',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.25)' : '#1a237e',
    transform: 'scale(1.05)',
    boxShadow: darkMode ? '0 0 20px rgba(255, 64, 129, 0.5)' : '0 0 20px rgba(33, 203, 243, 0.5)',
  },
}));

const MyPrivateContents: React.FC = () => {
  const { darkMode } = useThemeContext();
  const isAuthenticated = !!Cookies.get('token');
  const navigate = useNavigate();
  const [contents, setContents] = useState<any[]>([]);
  
  // Állapotok a Dialoghoz
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);

  const STATUS_CONFIG = {
    PRIVATE: { emoji: '🔒', color: darkMode ? '#ff4081' : '#2196f3' }
  };

  useEffect(() => {
    const fetchContent = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/searchmycontent`, {
          headers: { 'Authorization': `Bearer ${Cookies.get('token')}` }
        });
        const data = await response.json();
        setContents(data.filter((item: any) => item.status === 'PRIVATE'));
      } catch (error) {
        console.error("Failed to fetch content:", error);
      }
    };
    fetchContent();
  }, [isAuthenticated]);

  // Kezelőfüggvények
  const handleOpenContent = (content: any) => {
    setSelectedContent(content);
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleExited = () => {
    setSelectedContent(null);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <style>{keyframes}</style>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <Particles />
      </div>

      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      <LeftPanel
        isAuthenticated={isAuthenticated}
        onLoginRequired={() => navigate('/login')}
        onNavigation={(path) => navigate(path)}
        open={leftPanelOpen}
        onToggle={() => setLeftPanelOpen(prev => !prev)}
      />

      <Box component="main" sx={{
        position: 'relative', zIndex: 1, mt: '64px',
        ml: isSmallScreen ? 0 : (leftPanelOpen ? '240px' : '56px'),
        p: 3, transition: 'margin 0.3s ease'
      }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: theme.palette.text.primary }}>
          My Private Library
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: STATUS_CONFIG.PRIVATE.color }}>
            {STATUS_CONFIG.PRIVATE.emoji} PRIVATE
          </Typography>

          {contents.length > 0 ? (
            <List sx={{
              maxHeight: 600, overflowY: 'auto', borderRadius: 4, mt: 2,
              border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
              backdropFilter: 'blur(10px)', py: 0
            }}>
              {contents.map((result) => (
                <ListItem 
                  key={result.id} 
                  sx={{ 
                    borderBottom: '1px solid', borderColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                    cursor: 'pointer', transition: '0.2s', '&:hover': { bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }
                  }} 
                  // JAVÍTVA: handleOpenContent hívása setSelectedContent helyett
                  onClick={() => handleOpenContent(result)}
                >
                  <ListItemText
                    primary={<Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{result.title}</Typography>
                      <Chip label={result.category} size="small" sx={{ bgcolor: STATUS_CONFIG.PRIVATE.color, color: 'white', fontWeight: 'bold' }} />
                    </Stack>}
                    secondary={<Typography variant="body2" color="text.secondary" noWrap>{result.shortDescription}</Typography>}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      icon={<LinkIcon style={{ color: 'white' }} />}
                      label={result.url?.length > 15 ? `${result.url.slice(0, 15)}...` : result.url}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        window.open(result.url?.startsWith('http') ? result.url : `https://${result.url}`, '_blank'); 
                      }}
                      sx={{ bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', cursor: 'pointer' }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, fontStyle: 'italic', color: 'text.disabled' }}>
              No private items
            </Typography>
          )}
        </Box>

        {/* --- CONTENT PREVIEW DIALOG --- */}
        <AnimatedDialog 
          open={isDialogOpen} 
          onClose={handleClose} 
          darkMode={darkMode}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Zoom}
          transitionDuration={400}
          TransitionProps={{
            onExited: handleExited // Animáció után töröljük csak az adatokat
          }}
          disableScrollLock
        >
          <DialogTitle>
            📄 Content Preview
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {selectedContent && (
              <Stack spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {selectedContent.title}
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Chip label={selectedContent.category} sx={{ bgcolor: STATUS_CONFIG.PRIVATE.color, color: 'white', fontWeight: 'bold' }} />
                  </Stack>
                </Box>

                <Box sx={{ 
                  p: 3, borderRadius: '16px', 
                  bgcolor: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.6)',
                  border: '1px solid rgba(128,128,128,0.2)',
                }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {selectedContent.shortDescription || "No description provided."}
                  </Typography>
                </Box>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                   <Typography variant="caption" sx={{ color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                    Created at: 📅 {new Date(selectedContent.createdAt).toLocaleDateString()}
                  </Typography>
                  <Button 
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(selectedContent.url?.startsWith('http') ? selectedContent.url : `https://${selectedContent.url}`, '_blank')}
                    sx={{ color: STATUS_CONFIG.PRIVATE.color, fontWeight: 'bold', textTransform: 'none' }}
                  >
                    Open Source
                  </Button>
                </Stack>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
            <DialogActionButton darkMode={darkMode} onClick={handleClose}>
              Got it
            </DialogActionButton>
          </DialogActions>
        </AnimatedDialog>
      </Box>
    </div>
  );
};

export default MyPrivateContents;